/**
 * Comportamientos de Semana T (mejora progresiva: sin JS las páginas se ven completas y compran igual).
 * - Reloj tipo aeropuerto (dígitos que giran al cambiar).
 * - Taquilla: mazo deslizable sincronizado con pestañas, inclinación con el puntero, total que rueda,
 *   talón que se arranca al comprar, y barra fija que compra la boleta elegida.
 * - Pestañas de agenda, cifras que cuentan, parallax de la mariposa, acordeones suaves.
 * - Eventos para Microsoft Clarity.
 * Se reinicia cuando el editor de temas recarga una sección.
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;
  const pad = (n) => String(n).padStart(2, '0');
  const money = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
  const formatCents = (cents) => money.format(cents / 100).replace(/\s/g, '');

  const track = (name, tag) => {
    if (typeof window.clarity !== 'function') return;
    window.clarity('event', name);
    if (tag) window.clarity('set', name, String(tag));
  };

  // Reinicia una animación CSS de clase (para que se repita en cada cambio)
  const replay = (el, cls) => {
    if (reduceMotion || !el) return;
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
  };

  /* ---------- Reloj ---------- */
  function initCountdowns(root) {
    for (const el of root.querySelectorAll('[data-st-countdown]:not([data-st-ready])')) {
      el.dataset.stReady = 'true';
      const target = Date.parse(el.dataset.stCountdown);
      if (Number.isNaN(target)) continue;
      const parts = Object.fromEntries(['d', 'h', 'm', 's'].map((u) => [u, el.querySelector(`[data-unit="${u}"]`)]));
      let timer;
      const set = (node, value) => {
        if (!node || node.textContent === value) return;
        node.textContent = value;
        replay(node, 'is-flip');
      };
      const tick = () => {
        const diff = Math.max(0, target - Date.now());
        const s = Math.floor(diff / 1000);
        set(parts.d, pad(Math.floor(s / 86400)));
        set(parts.h, pad(Math.floor((s % 86400) / 3600)));
        set(parts.m, pad(Math.floor((s % 3600) / 60)));
        set(parts.s, pad(s % 60));
        if (diff === 0) {
          el.classList.add('is-finished');
          clearInterval(timer);
        }
      };
      tick();
      timer = setInterval(tick, 1000);
    }
  }

  /* ---------- Pestañas genéricas (agenda) ---------- */
  function bindTabKeys(list, tabs, select) {
    list.addEventListener('keydown', (e) => {
      const i = tabs.indexOf(document.activeElement);
      if (i < 0) return;
      if (e.key === 'ArrowRight') select(tabs[(i + 1) % tabs.length], true);
      else if (e.key === 'ArrowLeft') select(tabs[(i - 1 + tabs.length) % tabs.length], true);
      else return;
      e.preventDefault();
    });
  }

  function initTabs(root) {
    for (const list of root.querySelectorAll('[data-st-tabs]:not([data-st-ready])')) {
      list.dataset.stReady = 'true';
      const tabs = [...list.querySelectorAll('[role="tab"]')];
      const select = (tab, focus) => {
        for (const t of tabs) {
          const selected = t === tab;
          t.setAttribute('aria-selected', String(selected));
          t.tabIndex = selected ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !selected;
        }
        if (focus) tab.focus();
      };
      bindTabKeys(list, tabs, select);
      list.addEventListener('click', (e) => {
        const tab = e.target.closest('[role="tab"]');
        if (tab) select(tab, false);
      });
    }
  }

  /* ---------- Ir directo al checkout con solo lo elegido ----------
     Usa el enlace permanente de carrito de Shopify (/cart/VARIANTE:CANTIDAD): abre un checkout nuevo
     con únicamente ese producto, sin arrastrar lo que quedó de visitas anteriores. Sin JS, el
     formulario sigue funcionando con /cart/add y return_to=/checkout.
     Ciudad y departamento de facturación van ya llenos (Shopify los exige para pagar con Wompi);
     la dirección la escribe la persona y puede cambiar todo en el checkout. */
  const BILLING = {
    city: 'Barranquilla',
    province: 'Atlántico',
    country: 'CO',
  };

  function goCheckout(form) {
    const id = form.querySelector('[name="id"]')?.value;
    if (!id) return form.submit();
    const qty = Math.max(1, Number(form.querySelector('[name="quantity"]')?.value || 1));
    const root = window.Shopify?.routes?.root || '/';
    const billing = new URLSearchParams(Object.entries(BILLING).map(([k, v]) => [`checkout[billing_address][${k}]`, v]));
    window.location.assign(`${root}cart/${id}:${qty}?${billing}`);
  }

  function initBuyForms(root) {
    for (const form of root.querySelectorAll('form[data-st-buy-form]:not([data-st-ready])')) {
      form.dataset.stReady = 'true';
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (form.dataset.stSubmitting) return;
        form.dataset.stSubmitting = 'true';
        const ticket = form.closest('[data-st-ticket]');
        track('st_checkout', ticket?.dataset.stTier || form.dataset.stProduct || '');
        if (ticket && !reduceMotion) {
          ticket.classList.add('is-tearing');
          setTimeout(() => goCheckout(form), 420);
        } else {
          goCheckout(form);
        }
        // Si el usuario vuelve con el botón Atrás, la página queda lista para comprar de nuevo
        setTimeout(() => { delete form.dataset.stSubmitting; ticket?.classList.remove('is-tearing'); }, 4000);
      });
    }
  }

  /* ---------- Taquilla ---------- */
  function setActiveTicket(wrap, ticket, source) {
    const tickets = [...wrap.querySelectorAll('[data-st-ticket]')];
    if (!ticket || ticket.classList.contains('is-active')) return;
    for (const t of tickets) t.classList.toggle('is-active', t === ticket);
    for (const tab of wrap.querySelectorAll('[data-st-tix-tabs] [role="tab"]')) {
      const selected = tab.getAttribute('aria-controls') === ticket.id;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    }
    syncSticky();
    if (source) track('st_tier_select', ticket.dataset.stTier);
  }

  function scrollDeckTo(deck, ticket, smooth) {
    if (!deck || isDesktop()) return;
    const left = ticket.offsetLeft - (deck.clientWidth - ticket.clientWidth) / 2;
    deck.scrollTo({ left, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
  }

  function animateTotal(el, from, to) {
    if (reduceMotion || from === to) {
      el.textContent = formatCents(to);
      return;
    }
    const start = performance.now();
    const dur = 320;
    const step = (now) => {
      const k = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - k, 3);
      el.textContent = formatCents(Math.round((from + (to - from) * eased) / 100) * 100);
      if (k < 1) requestAnimationFrame(step);
    };
    replay(el, 'is-roll');
    requestAnimationFrame(step);
  }

  function initTickets(root) {
    for (const wrap of root.querySelectorAll('[data-st-tix]:not([data-st-ready])')) {
      wrap.dataset.stReady = 'true';
      wrap.classList.add('is-enhanced');
      const deck = wrap.querySelector('[data-st-tix-deck]');
      const tabsEl = wrap.querySelector('[data-st-tix-tabs]');
      const tabs = tabsEl ? [...tabsEl.querySelectorAll('[role="tab"]')] : [];
      const tickets = [...wrap.querySelectorAll('[data-st-ticket]')];
      if (!tickets.length) continue;

      // Abrir en la boleta recomendada sin animación
      const initial = wrap.querySelector('[data-st-ticket].is-active') || tickets[0];
      requestAnimationFrame(() => scrollDeckTo(deck, initial, false));

      // Pestañas → desplazar el mazo
      const selectTab = (tab, focus) => {
        const ticket = document.getElementById(tab.getAttribute('aria-controls'));
        setActiveTicket(wrap, ticket, 'tab');
        scrollDeckTo(deck, ticket, true);
        if (focus) tab.focus();
      };
      tabsEl?.addEventListener('click', (e) => {
        const tab = e.target.closest('[role="tab"]');
        if (tab) selectTab(tab, false);
      });
      if (tabsEl) bindTabKeys(tabsEl, tabs, selectTab);

      // Deslizar el mazo → actualizar pestaña (celular)
      if (deck && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (isDesktop()) return;
          const best = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (best) setActiveTicket(wrap, best.target, 'swipe');
        }, { root: deck, threshold: [0.6, 0.85] });
        for (const t of tickets) io.observe(t);
      }

      for (const ticket of tickets) {
        // En computador, la boleta que el usuario toca o mira pasa a ser la activa (barra fija)
        ticket.addEventListener('pointerenter', () => { if (isDesktop()) setActiveTicket(wrap, ticket, null); });
        ticket.addEventListener('focusin', () => setActiveTicket(wrap, ticket, null));

        // Inclinación 3D con puntero fino
        const inner = ticket.querySelector('.st-ticket__inner');
        if (inner && !reduceMotion && window.matchMedia('(pointer: fine)').matches) {
          ticket.addEventListener('pointermove', (e) => {
            const r = ticket.getBoundingClientRect();
            const x = (e.clientX - r.left) / r.width - 0.5;
            const y = (e.clientY - r.top) / r.height - 0.5;
            ticket.style.setProperty('--st-ry', `${(x * 8).toFixed(2)}deg`);
            ticket.style.setProperty('--st-rx', `${(-y * 6).toFixed(2)}deg`);
          });
          ticket.addEventListener('pointerleave', () => {
            ticket.style.setProperty('--st-ry', '0deg');
            ticket.style.setProperty('--st-rx', '0deg');
          });
        }

        // Total en vivo con número que rueda
        const input = ticket.querySelector('input[name="quantity"]');
        const total = ticket.querySelector('[data-st-total]');
        const price = Number(ticket.dataset.stPrice || 0);
        let last = price;
        const update = () => {
          const next = price * Math.max(1, Number(input?.value || 1));
          if (total && price) animateTotal(total, last, next);
          last = next;
          syncSticky();
        };
        input?.addEventListener('change', update);
        input?.addEventListener('input', update);
        ticket.addEventListener('st:qty', update);

      }
    }
  }

  function activeTicket() {
    const tix = document.querySelector('[data-st-tix]');
    if (!tix) return null;
    return tix.querySelector('[data-st-ticket].is-active') || tix.querySelector('[data-st-ticket]');
  }

  // Barra fija: muestra la boleta elegida y su total; el botón la compra directamente
  function syncSticky() {
    const bar = document.querySelector('[data-st-sticky]');
    if (!bar || bar.dataset.stMode !== 'tickets') return;
    const ticket = activeTicket();
    const label = bar.querySelector('[data-st-sticky-label]');
    const btn = bar.querySelector('[data-st-sticky-cta]');
    if (!ticket || !label || !btn) return;
    const soldout = ticket.dataset.stSoldout === 'true';
    const qty = Number(ticket.querySelector('input[name="quantity"]')?.value || 1);
    const price = Number(ticket.dataset.stPrice || 0);
    label.textContent = soldout
      ? `${ticket.dataset.stName} · Agotado`
      : `${ticket.dataset.stName}${qty > 1 ? ` × ${qty}` : ''} · ${price ? formatCents(price * qty) : ''}`;
    btn.firstChild.textContent = soldout ? 'Ver otras ' : 'Comprar ';
    btn.dataset.stTarget = soldout ? 'scroll' : 'buy';
  }

  function initQuantity(root) {
    for (const wrap of root.querySelectorAll('[data-st-qty]:not([data-st-ready])')) {
      wrap.dataset.stReady = 'true';
      const input = wrap.querySelector('input');
      if (!input) continue;
      wrap.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-step]');
        if (!btn) return;
        const min = Number(input.min || 1);
        const max = Number(input.max || 99);
        const next = Math.min(max, Math.max(min, Number(input.value || 1) + Number(btn.dataset.step)));
        if (String(next) === input.value) return;
        input.value = next;
        replay(input, 'is-roll');
        wrap.closest('[data-st-ticket]')?.dispatchEvent(new CustomEvent('st:qty'));
        track('st_qty_change', next);
      });
    }
  }

  /* ---------- Barra fija ---------- */
  function initSticky() {
    const bar = document.querySelector('[data-st-sticky]');
    if (!bar || bar.dataset.stReady) return;
    bar.dataset.stReady = 'true';
    const hero = document.querySelector('[data-st-hero]');
    const zones = [...document.querySelectorAll('[data-st-buy-zone]')];
    let heroVisible = Boolean(hero);
    const visible = new Set();
    const update = () => {
      const show = !heroVisible && visible.size === 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      bar.inert = !show;
      document.body.style.setProperty('--st-sticky-height', show ? `${bar.offsetHeight}px` : '0px');
    };
    if (hero) {
      new IntersectionObserver(([entry]) => {
        heroVisible = entry.isIntersecting;
        update();
      }).observe(hero);
    }
    if (zones.length) {
      const io = new IntersectionObserver((entries) => {
        for (const entry of entries) entry.isIntersecting ? visible.add(entry.target) : visible.delete(entry.target);
        update();
      }, { threshold: 0.2 });
      for (const zone of zones) io.observe(zone);
    }
    const cta = bar.querySelector('[data-st-sticky-cta]');
    cta?.addEventListener('click', (e) => {
      if (bar.dataset.stMode !== 'tickets' || cta.dataset.stTarget !== 'buy') return;
      const form = activeTicket()?.querySelector('form[data-st-buy-form]');
      if (!form) return;
      e.preventDefault();
      track('st_sticky_buy', activeTicket()?.dataset.stTier);
      form.requestSubmit();
    });
    syncSticky();
    update();
  }

  /* ---------- Aparición escalonada ---------- */
  function initReveal(root) {
    const items = [...root.querySelectorAll('[data-st-reveal]:not([data-st-ready])')];
    if (!items.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      for (const el of items) el.dataset.stReady = 'shown';
      return;
    }
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.dataset.stReady = 'shown';
        io.unobserve(entry.target);
      }
    }, { rootMargin: '0px 0px -8% 0px' });
    for (const el of items) {
      el.dataset.stReady = 'pending';
      io.observe(el);
    }
  }

  /* ---------- Cifras que cuentan ("+1.000", "8", "4ta") ---------- */
  function initCounters(root) {
    for (const el of root.querySelectorAll('[data-st-count]:not([data-st-ready])')) {
      el.dataset.stReady = 'true';
      const raw = el.textContent.trim();
      const match = raw.match(/^(\D*)([\d.]+)(.*)$/);
      if (!match || reduceMotion) continue;
      const [, prefix, digits, suffix] = match;
      const target = Number(digits.replace(/\./g, ''));
      const fmt = (n) => prefix + n.toLocaleString('es-CO') + suffix;
      el.textContent = fmt(0);
      const io = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        const start = performance.now();
        const dur = 1100;
        const step = (now) => {
          const k = Math.min(1, (now - start) / dur);
          el.textContent = fmt(Math.round(target * (1 - Math.pow(1 - k, 3))));
          if (k < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }, { threshold: 0.6 });
      io.observe(el);
    }
  }

  /* ---------- Parallax suave ---------- */
  function initParallax() {
    const items = [...document.querySelectorAll('[data-st-parallax]:not([data-st-ready])')];
    if (!items.length || reduceMotion) return;
    for (const el of items) el.dataset.stReady = 'true';
    let ticking = false;
    const run = () => {
      ticking = false;
      for (const el of items) {
        const factor = Number(el.dataset.stParallax || 0.15);
        const y = Math.min(window.scrollY, window.innerHeight * 1.2) * factor;
        el.style.transform = `translate3d(0, ${y.toFixed(1)}px, 0) rotate(${(y / 12).toFixed(1)}deg)`;
      }
    };
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(run);
    }, { passive: true });
  }

  /* ---------- Acordeones suaves (details con data-st-smooth) ---------- */
  function initSmoothDetails(root) {
    if (reduceMotion) return;
    for (const d of root.querySelectorAll('details[data-st-smooth]:not([data-st-ready])')) {
      d.dataset.stReady = 'true';
      const summary = d.querySelector('summary');
      const body = summary?.nextElementSibling;
      if (!summary || !body) continue;
      summary.addEventListener('click', (e) => {
        e.preventDefault();
        if (d.dataset.stAnimating) return;
        d.dataset.stAnimating = 'true';
        if (d.open) {
          const h = body.offsetHeight;
          body.animate([{ height: `${h}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 260, easing: 'ease-out' }).onfinish = () => {
            d.open = false;
            delete d.dataset.stAnimating;
          };
        } else {
          d.open = true;
          const h = body.offsetHeight;
          body.animate([{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }], { duration: 300, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' }).onfinish = () => {
            delete d.dataset.stAnimating;
          };
        }
      });
    }
  }

  /* ---------- Compra directa ----------
     - En la portada, cualquier botón amarillo que apunte a la taquilla abre una hoja con las boletas:
       un toque en "Comprar" va al checkout (sin JS, el enlace baja a la taquilla).
     - En las landings de producto, los botones que apuntan a la caja de compra envían su formulario
       directo al checkout. */
  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  function openSheet() {
    const tickets = [...document.querySelectorAll('[data-st-ticket]')];
    if (!tickets.length) return false;
    let dlg = document.getElementById('st-sheet');
    if (!dlg) {
      dlg = document.createElement('dialog');
      dlg.id = 'st-sheet';
      dlg.className = 'st-sheet';
      dlg.setAttribute('aria-labelledby', 'st-sheet-title');
      document.body.append(dlg);
      dlg.addEventListener('click', (e) => {
        if (e.target === dlg || e.target.closest('[data-st-sheet-close]')) dlg.close();
        const buy = e.target.closest('[data-st-sheet-buy]');
        if (!buy) return;
        const ticket = tickets[Number(buy.dataset.stSheetBuy)];
        const form = ticket?.querySelector('form[data-st-buy-form]');
        if (!form) return;
        buy.disabled = true;
        buy.textContent = 'Abriendo pago…';
        track('st_sheet_buy', ticket.dataset.stTier);
        goCheckout(form);
      });
    }
    const items = tickets.map((t, i) => {
      const soldout = t.dataset.stSoldout === 'true';
      const qty = Number(t.querySelector('input[name="quantity"]')?.value || 1);
      const price = Number(t.dataset.stPrice || 0);
      const recommended = Boolean(t.querySelector('.st-ticket__flag'));
      return `<li class="st-sheet__item st-sheet__item--${escapeHtml(t.dataset.stTier)}${soldout ? ' is-soldout' : ''}">
        <span class="st-sheet__dot" aria-hidden="true"></span>
        <span class="st-sheet__info">
          <span class="st-sheet__name">${escapeHtml(t.dataset.stName)}${recommended ? ' <em>Recomendado</em>' : ''}</span>
          <span class="st-sheet__price">${soldout ? 'Agotado' : formatCents(price * qty) + (qty > 1 ? ` · ${qty} boletas` : '')}</span>
        </span>
        ${soldout ? '' : `<button type="button" class="st-btn st-sheet__buy" data-st-sheet-buy="${i}">Comprar</button>`}
      </li>`;
    }).join('');
    dlg.innerHTML = `<div class="st-sheet__inner">
      <div class="st-sheet__head">
        <h2 id="st-sheet-title">Elige tu boleta</h2>
        <button type="button" class="st-sheet__close" data-st-sheet-close aria-label="Cerrar">${'×'}</button>
      </div>
      <ul class="st-sheet__list" role="list">${items}</ul>
      <p class="st-sheet__note">Pago seguro con Wompi · Confirmación al instante</p>
      <a class="st-link st-sheet__more" href="#caminos" data-st-sheet-close>Ver qué incluye cada una</a>
    </div>`;
    dlg.showModal();
    dlg.querySelector('.st-sheet__buy')?.focus();
    track('st_sheet_open');
    return true;
  }

  function initDirectBuy() {
    if (document.documentElement.dataset.stDirect) return;
    document.documentElement.dataset.stDirect = 'true';
    document.addEventListener('click', (e) => {
      if (e.defaultPrevented) return;
      const toTaquilla = e.target.closest('a.st-btn[href$="#caminos"]');
      if (toTaquilla && openSheet()) {
        e.preventDefault();
        return;
      }
      const toBox = e.target.closest('a[href^="#st-buy-"]');
      if (toBox) {
        const target = document.querySelector(toBox.getAttribute('href'));
        const form = target?.matches('form') ? target : target?.querySelector('form');
        if (!form || !form.querySelector('[type="submit"]')) return;
        e.preventDefault();
        track('st_direct_buy');
        form.requestSubmit();
      }
    });
  }

  /* ---------- Flechas de carruseles (escritorio) ---------- */
  function initScrollers(root) {
    root.querySelectorAll('[data-st-scroll]').forEach((btn) => {
      if (btn.dataset.stReady) return;
      btn.dataset.stReady = 'true';
      const track = btn.closest('section')?.querySelector('.st-speakers__track');
      if (!track) return;
      btn.addEventListener('click', () => {
        track.scrollBy({ left: Number(btn.dataset.stScroll) * track.clientWidth * 0.8, behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    });
  }

  /* ---------- Eventos declarativos ---------- */
  function initEvents() {
    if (document.documentElement.dataset.stEvents) return;
    document.documentElement.dataset.stEvents = 'true';
    document.addEventListener('click', (e) => {
      const el = e.target.closest('[data-st-event]');
      if (el) track(el.dataset.stEvent, el.dataset.stEventTag);
    });
    document.addEventListener('toggle', (e) => {
      const el = e.target;
      if (el instanceof HTMLDetailsElement && el.open && el.dataset.stEventToggle) track(el.dataset.stEventToggle);
    }, true);
  }

  function init(root = document) {
    initCountdowns(root);
    initTabs(root);
    initQuantity(root);
    initBuyForms(root);
    initTickets(root);
    initReveal(root);
    initCounters(root);
    initSmoothDetails(root);
    initScrollers(root);
    initParallax();
    initSticky();
    initDirectBuy();
    initEvents();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
